import { lazy, type ReactNode } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router';

import { HomePage } from '#src/App/HomePage.tsx';
import { OopsPage } from '#src/App/OopsPage.tsx';
import type { Breadcrumb } from '#src/components/Breadcrumbs/Breadcrumbs.tsx';
import { ExternalRedirect } from '#src/components/ExternalRedirect/ExternalRedirect.tsx';
import { PageErrorBoundary } from '#src/components/PageErrorBoundary/PageErrorBoundary.tsx';
import { AuthVerifier } from '#src/components/auth/AuthVerifier.tsx';
import {
  DASHBOARD_PAGES,
  getDashboardBreadcrumbs,
} from '#src/dashboard.config.ts';
import { RENDERABLE_PAGES, renderPage } from '#src/docs.tsx';
import { TrackingRouteWrapper } from '#src/hooks/tracking/TrackingRouteWrapper.tsx';
import { ClientErrorPage } from '#src/pages/ClientErrorPage.tsx';
import { AuthorizePage } from '#src/pages/authorization/AuthorizePage.tsx';
import { ResetSessionPage } from '#src/pages/authorization/ResetSessionPage.tsx';
import {
  ACCOUNT_SETUP_PATH,
  AccountSetupPage,
} from '#src/pages/manage/account-setup/AccountSetupPage.tsx';
import { CredentialsPage } from '#src/pages/manage/credentials/CredentialsPage.tsx';
import { EventsPage } from '#src/pages/manage/events/EventsPage.tsx';
import { HirersPage } from '#src/pages/manage/hirers/HirersPage.tsx';
import { DashboardHomePage } from '#src/pages/manage/home/DashboardHome.tsx';
import { JobAdsPage } from '#src/pages/manage/jobAds/JobAdsPage.tsx';
import { JobPostingPage } from '#src/pages/manage/jobPosting/JobPostingPage.tsx';
import { IntegrationHealthPage } from '#src/pages/manage/observability/IntegrationHealthPage/IntegrationHealthPage.tsx';
import { OrganizationsPage } from '#src/pages/manage/organizations/OrganizationsPage.tsx';
import { SecurityBreach } from '#src/pages/manage/security-breach/SecurityBreach.tsx';
import { UserProfilePage } from '#src/pages/manage/userProfile/UserProfilePage.tsx';
import { UsersPage } from '#src/pages/manage/users/UsersPage.tsx';
import { WebhooksPage } from '#src/pages/manage/webhooks/WebhooksPage.tsx';
import { WebhooksPlaygroundPage } from '#src/pages/manage/webhooksPlayground/WebhookPlaygroundPage.tsx';
import { NotificationsPage } from '#src/pages/notifications/NotificationsPage.tsx';
import { SchemaPage } from '#src/pages/schema/index.tsx';

const DASHBOARD_COMPONENTS: Record<
  keyof typeof DASHBOARD_PAGES,
  ({ breadcrumbs }: { breadcrumbs: Breadcrumb[] }) => ReactNode
> = {
  home: DashboardHomePage,
  credentials: CredentialsPage,
  polling: EventsPage,
  // GraphiQL explodes with SSR, so we need to dynamically import
  // https://github.com/graphql/graphiql/issues/4053
  graphQlExplorer: lazy(() =>
    import('#src/pages/manage/graphqlExplorer/index.tsx').then((mod) => ({
      default: mod.GraphqlExplorerPage,
    })),
  ),
  hirerRelationships: HirersPage,
  jobAds: JobAdsPage,
  notifications: NotificationsPage,
  jobPostingFeatures: JobPostingPage,
  users: UsersPage,
  profile: UserProfilePage,
  organizations: OrganizationsPage,
  webhookPlayground: WebhooksPlaygroundPage,
  integrationHealth: IntegrationHealthPage,
  webhookSubscriptions: WebhooksPage,
  securityIncident: SecurityBreach,
};

const withErrorBoundary =
  <T extends object>(Component: React.ComponentType<T>) =>
  (props: T) => (
    <PageErrorBoundary errorPage={<ClientErrorPage />}>
      <Component {...props} />
    </PageErrorBoundary>
  );

const routes = [
  {
    element: <Navigate replace to="/graphql/seek-api-conventions" />,
    path: '/graphql/in-the-seek-api',
  },
  {
    element: <Navigate replace to="/introduction#integration-process" />,
    path: '/introduction/development-and-debugging',
  },
  {
    element: <Navigate replace to="/guides/monitoring-and-troubleshooting" />,
    path: '/guides/monitoring',
  },
  {
    element: <HomePage />,
    path: '/',
  },
  {
    element: (
      <ExternalRedirect to="https://au.employer.seek.com/partners/terms-of-use" />
    ),
    path: '/introduction/terms-of-use',
  },
  {
    element: <SchemaPage />,
    path: '/schema/*',
  },
  {
    element: <ResetSessionPage />,
    path: '/reset-session',
  },
];

export const Router = () => (
  <TrackingRouteWrapper>
    <Routes>
      {routes.map(({ element, path }) => {
        const Component = withErrorBoundary(() => element);
        return <Route element={<Component />} key={path} path={path} />;
      })}
      {Object.values(RENDERABLE_PAGES).map((page) => {
        const Component = withErrorBoundary(() => renderPage(page));
        return (
          <Route element={<Component />} key={page.path} path={page.path} />
        );
      })}
      <Route
        Component={() => {
          const Component = withErrorBoundary(AccountSetupPage);
          return <Component />;
        }}
        path={ACCOUNT_SETUP_PATH}
      />
      <Route
        element={
          <AuthVerifier>
            <Outlet />
          </AuthVerifier>
        }
      >
        <Route path="/authorize" element={<AuthorizePage />} />

        {Object.entries(DASHBOARD_PAGES)
          .map(
            ([key, page]) =>
              [key as keyof typeof DASHBOARD_PAGES, page] as const,
          )
          .filter(
            ([, page]) => !('allowLoggedOut' in page && page.allowLoggedOut),
          )
          .map(([key, page]) => {
            const Component = withErrorBoundary(DASHBOARD_COMPONENTS[key]);
            return (
              <Route
                key={page.path}
                element={
                  <Component breadcrumbs={getDashboardBreadcrumbs(key)} />
                }
                path={'route' in page ? page.route : page.path}
              />
            );
          })}
      </Route>

      {Object.entries(DASHBOARD_PAGES)
        .map(
          ([key, page]) => [key as keyof typeof DASHBOARD_PAGES, page] as const,
        )
        .filter(([, page]) => 'allowLoggedOut' in page && page.allowLoggedOut)
        .map(([key, page]) => {
          const Component = withErrorBoundary(DASHBOARD_COMPONENTS[key]);
          return (
            <Route
              key={page.path}
              element={<Component breadcrumbs={getDashboardBreadcrumbs(key)} />}
              path={'route' in page ? page.route : page.path}
            />
          );
        })}

      <Route path="*" element={<OopsPage />} />
    </Routes>
  </TrackingRouteWrapper>
);
